import { Box, Typography, CircularProgress } from '@mui/material'
import { keyframes } from '@emotion/react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'



// Pulse animation for success states
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const TerminalStatus = ({ status, isProcessing }) => {
  if (!status && !isProcessing) {
    return null
  }

  const getStatusColor = (type) => {
    switch (type) {
      case 'success':
        return '#10b981' // Green
      case 'error':
        return '#ef4444' // Red
      case 'warning':
        return '#f59e0b' // Amber
      case 'info':
      default:
        return '#3b82f6' // Blue
    }
  }

  const getStatusIcon = (type) => {
    const iconProps = {
      sx: {
        fontSize: 32,
        color: getStatusColor(type),
      },
    }

    switch (type) {
      case 'success':
        return <CheckCircleIcon {...iconProps} />
      case 'error':
        return <ErrorIcon {...iconProps} />
      case 'warning':
        return <WarningIcon {...iconProps} />
      case 'info':
      default:
        return <InfoIcon {...iconProps} />
    }
  }

  const isLoading = isProcessing || status?.type === 'info'
  const statusColor = status ? getStatusColor(status.type) : '#3b82f6'

  return (
    <Box
      sx={{
        height: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: 3,
        border: `2px solid ${statusColor}20`,
        overflow: 'hidden',
      }}
    >
      {/* Background gradient effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at center, ${statusColor}08 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />

      {/* Main status circle */}
      <Box
        sx={{
          position: 'relative',
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: 'white',
          border: `3px solid ${statusColor}30`,
          boxShadow: `0 4px 20px ${statusColor}20`,
          zIndex: 2,
          animation:
            status?.type === 'success'
              ? `${pulse} 2s ease-in-out infinite`
              : 'none',
        }}
      >
        {/* Animated loading ring */}
        {isLoading && (
          <CircularProgress
            size={106}
            thickness={2}
            sx={{
              position: 'absolute',
              color: statusColor,
              animationDuration: '1.5s',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        )}

        {/* Status icon or loading spinner */}
        {isProcessing ? (
          <CircularProgress
            size={40}
            thickness={3}
            sx={{ color: statusColor }}
          />
        ) : status ? (
          getStatusIcon(status.type)
        ) : (
          <InfoIcon sx={{ fontSize: 32, color: statusColor }} />
        )}
      </Box>

      {/* Status message */}
      <Typography
        variant='body1'
        sx={{
          mt: 2,
          textAlign: 'center',
          color: statusColor,
          fontWeight: 500,
          px: 2,
          zIndex: 2,
          position: 'relative',
        }}
      >
        {isProcessing ? 'Processing...' : status?.message || 'Ready'}
      </Typography>

      {/* Animated dots for processing states */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            mt: 1,
            zIndex: 2,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: statusColor,
                animation: `${pulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default TerminalStatus
